import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const updates = {};
      if (name !== user?.name) updates.name = name;
      if (email !== user?.email) updates.email = email;
      if (Object.keys(updates).length === 0) {
        toast.info('No changes to save');
        setSavingProfile(false);
        return;
      }
      await api.patch('/users/me', updates);
      if (refreshUser) await refreshUser();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    setSavingPassword(true);
    try {
      await api.post('/users/me/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password changed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const ROLE_LABELS = { global_admin: 'Global Admin', admin: 'Admin', staff: 'Staff' };

  return (
    <div className="p-6 max-w-2xl" data-testid="profile-page">
      <p className="text-sm text-zinc-500 mb-6">Manage your account details</p>

      <Card className="rounded-sm border-zinc-200 shadow-none mb-6">
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-12 h-12 rounded-sm flex items-center justify-center text-lg font-bold bg-zinc-100 text-zinc-600">
              {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : '?'}
            </div>
            <div>
              <div className="text-base font-bold text-zinc-900">{user?.name}</div>
              <div className="text-xs text-zinc-500">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
          </div>

          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} className="rounded-sm h-9" required data-testid="profile-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Email address</Label>
              <Input type="email" value={email} onChange={e => setEmail(e.target.value)} className="rounded-sm h-9" required data-testid="profile-email" />
            </div>
            <Button type="submit" disabled={savingProfile} data-testid="save-profile-btn" className="rounded-sm text-sm"
              style={{ background: 'var(--brand-primary, #1C3557)' }}>
              {savingProfile ? 'Saving...' : 'Save changes'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="rounded-sm border-zinc-200 shadow-none">
        <CardContent className="p-6">
          <h2 className="text-sm font-bold text-zinc-900 mb-4">Change password</h2>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Current password</Label>
              <Input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="rounded-sm h-9" required data-testid="current-password" />
            </div>
            <Separator />
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">New password</Label>
              <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="rounded-sm h-9" required data-testid="new-password" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Confirm new password</Label>
              <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="rounded-sm h-9" required data-testid="confirm-password" />
            </div>
            <Button type="submit" disabled={savingPassword} data-testid="change-password-btn" className="rounded-sm text-sm"
              style={{ background: 'var(--brand-primary, #1C3557)' }}>
              {savingPassword ? 'Changing...' : 'Change password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
